import { useState, useEffect } from "react";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { 
  DollarSign, 
  Plus, 
  TrendingUp, 
  TrendingDown,
  PieChart,
  FileText,
  Wrench,
  Users,
  Package,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

export const BudgetPage = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [budgetLines, setBudgetLines] = useState([]);
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [costDialogOpen, setCostDialogOpen] = useState(false);

  const [budgetForm, setBudgetForm] = useState({
    category: "labor",
    description: "",
    budgeted_amount: 0
  });

  const today = new Date().toISOString().split('T')[0];
  const [costForm, setCostForm] = useState({
    category: "labor",
    description: "",
    amount: 0,
    date: today,
    invoice_number: "",
    supplier: ""
  });

  const categories = [
    { value: "labor", label: language === "sv" ? "Arbetskostnad" : "Labor", icon: Users, color: "bg-blue-500" },
    { value: "material", label: language === "sv" ? "Material" : "Material", icon: Package, color: "bg-green-500" },
    { value: "subcontractor", label: language === "sv" ? "Underentreprenör" : "Subcontractor", icon: Wrench, color: "bg-amber-500" },
    { value: "equipment", label: language === "sv" ? "Utrustning" : "Equipment", icon: Wrench, color: "bg-purple-500" },
    { value: "other", label: language === "sv" ? "Övrigt" : "Other", icon: FileText, color: "bg-slate-500" }
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchBudgetData(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
        if (data.length > 0) {
          setSelectedProject(data[0].project_id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetData = async (projectId) => {
    setLoading(true);
    try {
      const [summaryRes, linesRes, costsRes] = await Promise.all([
        fetch(`${API}/projects/${projectId}/budget-summary`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include"
        }),
        fetch(`${API}/projects/${projectId}/budget-lines`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include"
        }),
        fetch(`${API}/projects/${projectId}/costs`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include"
        })
      ]);

      if (summaryRes.ok) setBudgetSummary(await summaryRes.json());
      if (linesRes.ok) setBudgetLines(await linesRes.json());
      if (costsRes.ok) setCosts(await costsRes.json());
    } catch (err) {
      console.error("Failed to fetch budget data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBudgetSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;

    try {
      const res = await fetch(`${API}/projects/${selectedProject}/budget-lines`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify(budgetForm)
      });

      if (res.ok) {
        toast.success(language === "sv" ? "Budgetrad tillagd!" : "Budget line added!");
        setBudgetDialogOpen(false);
        setBudgetForm({ category: "labor", description: "", budgeted_amount: 0 });
        fetchBudgetData(selectedProject);
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  const handleCostSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;

    try {
      const res = await fetch(`${API}/projects/${selectedProject}/costs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify(costForm)
      });

      if (res.ok) {
        toast.success(language === "sv" ? "Kostnad registrerad!" : "Cost registered!");
        setCostDialogOpen(false);
        setCostForm({ category: "labor", description: "", amount: 0, date: today, invoice_number: "", supplier: "" });
        fetchBudgetData(selectedProject);
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(amount);
  };

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6" data-testid="budget-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" ? "Budget & Ekonomi" : "Budget & Economy"}
            </h1>
            <p className="text-slate-600 mt-1">
              {language === "sv" 
                ? "Följ upp budget, kostnader och prognoser" 
                : "Track budget, costs and forecasts"}
            </p>
          </div>

          <div className="flex gap-2">
            <Dialog open={budgetDialogOpen} onOpenChange={setBudgetDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" disabled={!selectedProject} data-testid="add-budget-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {language === "sv" ? "Budget" : "Budget"}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{language === "sv" ? "Lägg till budgetrad" : "Add budget line"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleBudgetSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>{language === "sv" ? "Kategori" : "Category"}</Label>
                    <Select value={budgetForm.category} onValueChange={(v) => setBudgetForm({...budgetForm, category: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === "sv" ? "Beskrivning" : "Description"}</Label>
                    <Input 
                      value={budgetForm.description}
                      onChange={(e) => setBudgetForm({...budgetForm, description: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === "sv" ? "Budgeterat belopp (SEK)" : "Budgeted amount (SEK)"}</Label>
                    <Input 
                      type="number"
                      value={budgetForm.budgeted_amount}
                      onChange={(e) => setBudgetForm({...budgetForm, budgeted_amount: parseFloat(e.target.value) || 0})}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full">{language === "sv" ? "Lägg till" : "Add"}</Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={costDialogOpen} onOpenChange={setCostDialogOpen}>
              <DialogTrigger asChild>
                <Button disabled={!selectedProject} data-testid="add-cost-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {language === "sv" ? "Kostnad" : "Cost"}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{language === "sv" ? "Registrera kostnad" : "Register cost"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCostSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>{language === "sv" ? "Kategori" : "Category"}</Label>
                    <Select value={costForm.category} onValueChange={(v) => setCostForm({...costForm, category: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === "sv" ? "Beskrivning" : "Description"}</Label>
                    <Input 
                      value={costForm.description}
                      onChange={(e) => setCostForm({...costForm, description: e.target.value})}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === "sv" ? "Belopp (SEK)" : "Amount (SEK)"}</Label>
                      <Input 
                        type="number"
                        value={costForm.amount}
                        onChange={(e) => setCostForm({...costForm, amount: parseFloat(e.target.value) || 0})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === "sv" ? "Datum" : "Date"}</Label>
                      <Input 
                        type="date"
                        value={costForm.date}
                        onChange={(e) => setCostForm({...costForm, date: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === "sv" ? "Fakturanummer" : "Invoice #"}</Label>
                      <Input 
                        value={costForm.invoice_number}
                        onChange={(e) => setCostForm({...costForm, invoice_number: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === "sv" ? "Leverantör" : "Supplier"}</Label>
                      <Input 
                        value={costForm.supplier}
                        onChange={(e) => setCostForm({...costForm, supplier: e.target.value})}
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full">{language === "sv" ? "Registrera" : "Register"}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Project Filter */}
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-full sm:w-[300px] h-12" data-testid="budget-project-select">
            <SelectValue placeholder={language === "sv" ? "Välj projekt" : "Select project"} />
          </SelectTrigger>
          <SelectContent>
            {projects.map(p => (
              <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : !selectedProject ? (
          <Card>
            <CardContent className="py-12 text-center">
              <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">{language === "sv" ? "Välj ett projekt" : "Select a project"}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <PieChart className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                        {formatCurrency(budgetSummary?.total_budgeted || 0)}
                      </p>
                      <p className="text-xs text-slate-600">{language === "sv" ? "Budgeterat" : "Budgeted"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                        {formatCurrency(budgetSummary?.total_actual || 0)}
                      </p>
                      <p className="text-xs text-slate-600">{language === "sv" ? "Faktiskt" : "Actual"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className={budgetSummary?.is_over_budget ? "border-red-200 bg-red-50" : ""}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${budgetSummary?.variance >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                      {budgetSummary?.variance >= 0 
                        ? <TrendingUp className="w-5 h-5 text-green-600" />
                        : <TrendingDown className="w-5 h-5 text-red-600" />
                      }
                    </div>
                    <div>
                      <p className={`text-lg font-bold ${budgetSummary?.variance >= 0 ? 'text-green-600' : 'text-red-600'}`} style={{ fontFamily: 'Barlow Condensed' }}>
                        {formatCurrency(budgetSummary?.variance || 0)}
                      </p>
                      <p className="text-xs text-slate-600">{language === "sv" ? "Avvikelse" : "Variance"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <FileText className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                        {formatCurrency(budgetSummary?.approved_ata || 0)}
                      </p>
                      <p className="text-xs text-slate-600">{language === "sv" ? "Godkänd ÄTA" : "Approved CO"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Budget Progress */}
            {budgetSummary?.total_budgeted > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                    {language === "sv" ? "Budgetförbrukning" : "Budget Usage"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600">
                      {Math.round((budgetSummary.total_actual / budgetSummary.total_budgeted) * 100)}%
                    </span>
                    {budgetSummary.is_over_budget && (
                      <Badge className="bg-red-100 text-red-800">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {language === "sv" ? "Överbudget" : "Over budget"}
                      </Badge>
                    )}
                  </div>
                  <Progress 
                    value={Math.min((budgetSummary.total_actual / budgetSummary.total_budgeted) * 100, 100)} 
                    className={`h-3 ${budgetSummary.is_over_budget ? '[&>div]:bg-red-500' : ''}`}
                  />
                </CardContent>
              </Card>
            )}

            {/* Tabs for Budget Lines and Costs */}
            <Tabs defaultValue="costs" className="space-y-4">
              <TabsList>
                <TabsTrigger value="costs">{language === "sv" ? "Kostnader" : "Costs"}</TabsTrigger>
                <TabsTrigger value="budget">{language === "sv" ? "Budget" : "Budget"}</TabsTrigger>
                <TabsTrigger value="categories">{language === "sv" ? "Per kategori" : "By category"}</TabsTrigger>
              </TabsList>

              <TabsContent value="costs" className="space-y-3">
                {costs.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-slate-500">
                      {language === "sv" ? "Inga kostnader registrerade" : "No costs registered"}
                    </CardContent>
                  </Card>
                ) : (
                  costs.slice(0, 20).map(cost => {
                    const cat = categories.find(c => c.value === cost.category);
                    return (
                      <Card key={cost.cost_id}>
                        <CardContent className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${cat?.color || 'bg-slate-500'} bg-opacity-10`}>
                              {cat && <cat.icon className={`w-4 h-4 ${cat.color.replace('bg-', 'text-')}`} />}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{cost.description}</p>
                              <p className="text-sm text-slate-500">{cost.date} {cost.supplier && `• ${cost.supplier}`}</p>
                            </div>
                          </div>
                          <p className="font-bold text-slate-900">{formatCurrency(cost.amount)}</p>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </TabsContent>

              <TabsContent value="budget" className="space-y-3">
                {budgetLines.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-slate-500">
                      {language === "sv" ? "Ingen budget definierad" : "No budget defined"}
                    </CardContent>
                  </Card>
                ) : (
                  budgetLines.map(line => {
                    const cat = categories.find(c => c.value === line.category);
                    return (
                      <Card key={line.budget_id}>
                        <CardContent className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline">{cat?.label || line.category}</Badge>
                            <span className="text-slate-900">{line.description}</span>
                          </div>
                          <p className="font-bold text-slate-900">{formatCurrency(line.budgeted_amount)}</p>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </TabsContent>

              <TabsContent value="categories">
                <div className="grid sm:grid-cols-2 gap-4">
                  {categories.map(cat => {
                    const budgeted = budgetSummary?.budgeted_by_category?.[cat.value] || 0;
                    const actual = budgetSummary?.by_category?.[cat.value] || 0;
                    const percent = budgeted > 0 ? Math.round((actual / budgeted) * 100) : 0;
                    
                    return (
                      <Card key={cat.value}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <div className={`p-1.5 rounded ${cat.color}`}>
                              <cat.icon className="w-4 h-4 text-white" />
                            </div>
                            <span className="font-medium">{cat.label}</span>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500">{language === "sv" ? "Budgeterat" : "Budgeted"}</span>
                              <span>{formatCurrency(budgeted)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500">{language === "sv" ? "Faktiskt" : "Actual"}</span>
                              <span className={actual > budgeted ? 'text-red-600 font-medium' : ''}>{formatCurrency(actual)}</span>
                            </div>
                            {budgeted > 0 && (
                              <Progress value={Math.min(percent, 100)} className="h-2" />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </AppLayout>
  );
};
